// src/pages/Admin/CompanyDocuments.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, clearStoredCredentials, getAuthHeaders } from '../../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api';

function CompanyDocuments() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Modal state for document upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState('contract');

  // Check admin auth on mount
  useEffect(() => {
    const adminSession = localStorage.getItem('fsl_admin_session');
    if (!adminSession) {
      toast.error('Please log in as admin');
      navigate('/admin-login', { replace: true });
      return;
    }
    fetchCompanies();
  }, [navigate]);

  // ✅ Fetch companies with document info
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(
        `${API_BASE}/hmo/list-onboarded-companies`, 
        'POST', 
        {}
      );
      
      if (response?.status === 'success') {
        const formattedCompanies = (response.data || []).map(company => ({
          companyId: company.companyId,
          businessName: company.businessName || 'N/A',
          title: company.title || 'N/A',
          startDate: company.startDate ? new Date(company.startDate).toLocaleDateString('en-NG') : 'N/A',
          endDate: company.endDate ? new Date(company.endDate).toLocaleDateString('en-NG') : 'N/A',
          startDateRaw: company.startDate,
          endDateRaw: company.endDate,
          
          // Document info
          hasDocument: !!company.documentPath,
          documentPath: company.documentPath || null,
          documentName: company.documentName || null,
          documentType: company.documentType || null,
          documentUploadedAt: company.documentUploadedAt ? new Date(company.documentUploadedAt).toLocaleDateString('en-NG') : null,
          
          _full: company
        }));
        setCompanies(formattedCompanies);
      }
    } catch (error) {
      console.error('💥 Fetch companies error:', error);
      toast.error('Could not load companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle file selection for upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, DOC, DOCX, XLS, XLSX');
      setDocumentFile(null);
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      setDocumentFile(null);
      return;
    }
    
    setDocumentFile(file);
    toast.success('File selected: ' + file.name);
  };

  // ✅ Upload document using DIRECT FETCH (avoids authenticatedFetch signal bug)
  const handleUploadDocument = async () => {
    if (!selectedCompany || !documentFile) {
      toast.error('Please select a company and file');
      return;
    }
    
    if (!documentTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('companyId', selectedCompany.companyId);
      formData.append('documentType', documentType);
      formData.append('documentTitle', documentTitle.trim());
      formData.append('file', documentFile);
      
      // ✅ Get auth headers from centralized utility
      const headers = getAuthHeaders();
      
      if (!headers) {
        throw new Error('Not authenticated. Please log in again.');
      }
      
      // ✅ DEBUG: Log what we're sending
      console.log('🔐 UPLOAD DEBUG:', {
        endpoint: `${API_BASE}/hmo/attach-company-document`,
        companyId: selectedCompany.companyId,
        fileName: documentFile.name,
        fileSize: documentFile.size,
        hasAuthHeaders: !!headers.AuthorizationKey && !!headers.Token && !!headers.SecretKey
      });
      
      // ✅ Use DIRECT fetch with FormData - NO authenticatedFetch to avoid signal bug
      const response = await fetch(`${API_BASE}/hmo/attach-company-document`, {
        method: 'POST',
        headers: {
          'AuthorizationKey': headers.AuthorizationKey,
          'Token': headers.Token,
          'SecretKey': headers.SecretKey,
          // ✅ DO NOT set Content-Type - browser adds boundary for FormData
        },
        body: formData,
        // ✅ NO signal option to avoid AbortSignal conversion error
      });
      
      // ✅ Parse response
      let result;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        try {
          result = JSON.parse(text);
        } catch {
          result = { status: 'error', message: text || `HTTP ${response.status}` };
        }
      }
      
      if (!response.ok || result?.status !== 'success') {
        console.error('❌ Upload failed:', result);
        throw new Error(result?.message || `Upload failed: ${response.status}`);
      }
      
      toast.success('Document uploaded successfully!');
      handleCloseUploadModal();
      fetchCompanies(); // Refresh list
      
    } catch (error) {
      console.error('💥 Upload error:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // ✅ Download document
  const handleDownloadDocument = async (company) => {
    if (!company.documentPath) {
      toast.error('No document available');
      return;
    }
    
    try {
      const fileUrl = `${API_BASE}${company.documentPath}`;
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  // ✅ Delete document
  const handleDeleteDocument = async (companyId, companyName) => {
    if (!window.confirm(`Remove document for "${companyName}"?`)) return;
    
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/hmo/delete-company-document`,
        'DELETE',
        { companyId }
      );
      
      if (response?.status === 'success') {
        toast.success('Document removed successfully');
        fetchCompanies();
      } else {
        throw new Error(response?.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete document');
    }
  };

  // ✅ Open upload modal
  const handleOpenUploadModal = (company) => {
    setSelectedCompany(company);
    setDocumentTitle(company.title !== 'N/A' ? `${company.title} - ${company.businessName}` : company.businessName);
    setDocumentType('contract');
    setDocumentFile(null);
    setShowUploadModal(true);
  };

  // ✅ Close upload modal
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedCompany(null);
    setDocumentFile(null);
    setDocumentTitle('');
    setDocumentType('contract');
  };

  // ✅ Filter companies
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesSearch = 
        company.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.title?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = 
        filterStatus === 'all' ||
        (filterStatus === 'with-doc' && company.hasDocument) ||
        (filterStatus === 'without-doc' && !company.hasDocument);
      
      let matchesDate = true;
      if (dateRange.start && company.startDateRaw) {
        const start = new Date(company.startDateRaw);
        const filterStart = new Date(dateRange.start);
        if (start < filterStart) matchesDate = false;
      }
      if (dateRange.end && company.endDateRaw) {
        const end = new Date(company.endDateRaw);
        const filterEnd = new Date(dateRange.end);
        if (end > filterEnd) matchesDate = false;
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [companies, searchTerm, filterStatus, dateRange]);

  // ✅ Handle Logout
  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('fsl_admin_session');
    toast.success('Logged out successfully');
    navigate('/admin-login', { replace: true });
  };

  // ✅ Navigation Handler
  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
    if (window.innerWidth < 992) setSidebarOpen(false);
  };

  // ✅ Nav Items
  const navItems = useMemo(() => {
    const currentPath = location.pathname;
    return [
      { 
        section: 'MAIN', 
        items: [
          { name: 'Get Started', icon: 'fa-home', path: '/get-started', active: currentPath === '/get-started' }
        ] 
      },
      { 
        section: 'DASHBOARD', 
        items: [
          { name: 'Analytics', icon: 'fa-chart-bar', path: '/hmoadmin', active: currentPath === '/hmoadmin' },
        ] 
      },
      {
        section: 'ADMIN',
        items: [
          { name: 'Client', icon: 'fa-building', path: '/admin/companies', active: currentPath === '/admin/companies' },
          { name: 'Send Document', icon: 'fa-file-contract', path: '/admin/company-documents', active: currentPath.startsWith('/admin/company-documents') },
          { name: 'Settings', icon: 'fa-cog', path: '/admin/crud/title', active: currentPath.startsWith('/admin/crud') }
        ]
      }
    ];
  }, [location.pathname]);

  // ✅ Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // ✅ Get file icon based on type
  const getFileIcon = (fileName) => {
    if (!fileName) return 'fa-file';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'fa-file-pdf';
      case 'doc':
      case 'docx': return 'fa-file-word';
      case 'xls':
      case 'xlsx': return 'fa-file-excel';
      default: return 'fa-file';
    }
  };

  return (
    <div className="admin-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* ✅ SIDEBAR */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="brand-logo">
            <img src="../images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
          </Link>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link 
                      to={item.path || '#'} 
                      className={`nav-link ${item.active ? 'active' : ''}`}
                      onClick={(e) => item.path && handleNavClick(item.path, e)}
                    >
                      <i className={`fas ${item.icon} me-2`}></i>
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <button className="nav-link" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>
                Logout
              </button>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>Company Documents</h4>
            </div>
          </div>
          <div className="header-right">
            <button className="header-icon"><i className="fas fa-search"></i></button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">2</span></button>
            <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #F89423 0%, #F89423 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', cursor: 'pointer' }}>AD</div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          {/* Header */}
          <div style={{ marginBottom: '24px' }}>
            <h5 className="mb-1">Manage Company Documents</h5>
            <p className="text-muted small mb-0">Upload and manage contracts, agreements, and other documents for each company</p>
          </div>

          {/* Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '12px', 
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
              <input 
                type="text" 
                placeholder="Search company or title..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                style={{ paddingLeft: '40px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', width: '100%', outline: 'none' }} 
              />
            </div>
            
            {/* Document Status Filter */}
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', backgroundColor: 'white' }}
            >
              <option value="all">All Documents</option>
              <option value="with-doc">With Document</option>
              <option value="without-doc">Without Document</option>
            </select>
            
            {/* Start Date Filter */}
            <input 
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              placeholder="Start date"
              style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
            />
            
            {/* End Date Filter */}
            <input 
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              placeholder="End date"
              style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
            />
            
            {/* Clear Filters */}
            <button 
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setDateRange({ start: '', end: '' });
              }}
              style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', background: 'white', color: '#6b7280', cursor: 'pointer' }}
            >
              <i className="fas fa-times me-2"></i>Clear
            </button>
          </div>

          {/* Companies Table */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-3 text-muted">Loading companies...</p>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-file-contract fa-3x mb-3 opacity-25"></i>
                <h5>No companies found</h5>
                <p>Try adjusting your filters or search terms</p>
              </div>
            ) : (
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151', width: '50px' }}>#</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Company</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Title</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Start Date</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>End Date</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Document</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151', width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company, index) => (
                    <tr key={company.companyId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td className="py-3 px-4">{index + 1}</td>
                      <td className="py-3 px-4" style={{ fontWeight: '500' }}>
                        {company.businessName}
                      </td>
                      <td className="py-3 px-4" style={{ fontSize: '13px', color: '#6b7280' }}>
                        {company.title}
                      </td>
                      <td className="py-3 px-4" style={{ fontSize: '13px' }}>
                        <span style={{ color: '#10b981', fontWeight: '500' }}>{company.startDate}</span>
                      </td>
                      <td className="py-3 px-4" style={{ fontSize: '13px' }}>
                        <span style={{ 
                          color: company.endDate !== 'N/A' && new Date(company.endDateRaw) < new Date() ? '#ef4444' : '#6b7280', 
                          fontWeight: '500' 
                        }}>
                          {company.endDate}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {company.hasDocument ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <i className={`fas ${getFileIcon(company.documentName)} text-primary`}></i>
                            <span style={{ fontSize: '13px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {company.documentName}
                            </span>
                            <span className="badge bg-success" style={{ fontSize: '11px', padding: '2px 8px' }}>✓</span>
                          </div>
                        ) : (
                          <span className="text-muted" style={{ fontSize: '13px' }}>No document</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="btn-group btn-group-sm">
                          {company.hasDocument ? (
                            <>
                              <button 
                                className="btn btn-outline-primary btn-sm" 
                                onClick={() => handleDownloadDocument(company)}
                                title="Download"
                                style={{ 
                                  border: '1px solid #3b82f6', 
                                  color: '#3b82f6', 
                                  background: 'white', 
                                  padding: '4px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer',
                                  marginRight: '4px'
                                }}
                              >
                                <i className="fas fa-download"></i>
                              </button>
                              <button 
                                className="btn btn-outline-danger btn-sm" 
                                onClick={() => handleDeleteDocument(company.companyId, company.businessName)}
                                title="Delete"
                                style={{ 
                                  border: '1px solid #ef4444', 
                                  color: '#ef4444', 
                                  background: 'white', 
                                  padding: '4px 12px', 
                                  borderRadius: '4px', 
                                  cursor: 'pointer'
                                }}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </>
                          ) : (
                            <button 
                              className="btn btn-outline-success btn-sm" 
                              onClick={() => handleOpenUploadModal(company)}
                              title="Upload Document"
                              style={{ 
                                border: '1px solid #10b981', 
                                color: '#10b981', 
                                background: 'white', 
                                padding: '4px 12px', 
                                borderRadius: '4px', 
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="fas fa-upload"></i> Upload
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Results count */}
          <div style={{ marginTop: '16px', fontSize: '13px', color: '#6b7280' }}>
            Showing {filteredCompanies.length} of {companies.length} companies
          </div>
        </div>
      </div>

      {/* ✅ Upload Document Modal */}
      {showUploadModal && selectedCompany && (
        <div className="modal-overlay" onClick={handleCloseUploadModal} style={{ 
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
          background: 'rgba(0,0,0,0.5)', zIndex: 2000, 
          display: 'flex', alignItems: 'center', justifyContent: 'center' 
        }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ 
            background: 'white', borderRadius: '12px', width: '90%', maxWidth: '500px', 
            maxHeight: '90vh', overflowY: 'auto' 
          }}>
            <div className="modal-header" style={{ 
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb', 
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
            }}>
              <h5 style={{ margin: 0, color: '#1f2937' }}>Upload Document</h5>
              <button onClick={handleCloseUploadModal} style={{ 
                background: 'none', border: 'none', fontSize: '24px', 
                color: '#6b7280', cursor: 'pointer' 
              }}>×</button>
            </div>
            
            <div style={{ padding: '24px' }}>
              {/* Company Info */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                marginBottom: '20px' 
              }}>
                <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '14px' }}>
                  {selectedCompany.businessName}
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                  {selectedCompany.startDate} → {selectedCompany.endDate}
                </p>
              </div>
              
              {/* Document Title */}
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ 
                  display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' 
                }}>
                  Document Title <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input 
                  type="text" 
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="e.g., Service Agreement 2024"
                  style={{ 
                    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', 
                    borderRadius: '8px', fontSize: '14px' 
                  }} 
                />
              </div>
              
              {/* Document Type */}
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ 
                  display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' 
                }}>
                  Document Type
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  style={{ 
                    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', 
                    borderRadius: '8px', fontSize: '14px', backgroundColor: 'white' 
                  }}
                >
                  <option value="contract">Contract</option>
                  <option value="agreement">Agreement</option>
                  <option value="invoice">Invoice</option>
                  <option value="report">Report</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              {/* File Upload */}
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ 
                  display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' 
                }}>
                  Select File <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ 
                  border: '2px dashed #d1d5db', borderRadius: '8px', padding: '20px', 
                  textAlign: 'center', backgroundColor: '#f9fafb' 
                }}>
                  {documentFile ? (
                    <div>
                      <i className={`fas ${getFileIcon(documentFile.name)} fa-2x text-primary mb-2`}></i>
                      <p style={{ margin: '8px 0', fontWeight: '500', fontSize: '14px' }}>
                        {documentFile.name}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                        {formatFileSize(documentFile.size)}
                      </p>
                      <button 
                        type="button"
                        onClick={() => setDocumentFile(null)}
                        style={{ 
                          marginTop: '12px', padding: '6px 12px', fontSize: '12px',
                          background: '#fee2e2', color: '#ef4444', border: 'none',
                          borderRadius: '4px', cursor: 'pointer'
                        }}
                      >
                        <i className="fas fa-times me-1"></i> Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt fa-2x" style={{ color: '#9ca3af', marginBottom: '12px' }}></i>
                      <p style={{ margin: '0 0 8px', fontSize: '14px' }}>Drag & drop or click to browse</p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                        PDF, DOC, DOCX, XLS, XLSX • Max 10MB
                      </p>
                      <input 
                        type="file" 
                        id="document-upload"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        style={{ display: 'none' }}
                      />
                      <label 
                        htmlFor="document-upload"
                        style={{ 
                          display: 'inline-block', marginTop: '12px', padding: '8px 16px',
                          background: '#F89423', color: 'white', borderRadius: '6px',
                          fontSize: '14px', cursor: 'pointer', fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-folder-open me-1"></i> Select File
                      </label>
                    </>
                  )}
                </div>
              </div>
              
              {/* Info Box */}
              <div style={{ 
                padding: '12px', backgroundColor: '#fef3e2', borderRadius: '8px', 
                marginBottom: '20px', fontSize: '13px', color: '#92400e' 
              }}>
                <i className="fas fa-info-circle me-2"></i>
                Uploaded documents are stored securely and can be downloaded anytime.
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button 
                  type="button" 
                  onClick={handleCloseUploadModal} 
                  disabled={uploading}
                  style={{ 
                    padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', 
                    borderRadius: '8px', color: '#374151', cursor: uploading ? 'not-allowed' : 'pointer',
                    fontWeight: '500', opacity: uploading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={handleUploadDocument}
                  disabled={!documentFile || !documentTitle.trim() || uploading}
                  style={{ 
                    padding: '10px 20px', background: '#F89423', border: 'none', 
                    borderRadius: '8px', color: 'white', cursor: (!documentFile || !documentTitle.trim() || uploading) ? 'not-allowed' : 'pointer',
                    fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
                    opacity: (!documentFile || !documentTitle.trim() || uploading) ? 0.7 : 1
                  }}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload"></i> Upload Document
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ CSS */}
      <style>{`
        .admin-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        
        /* Sidebar */
        .sidebar {
          width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0;
          height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000;
          border-right: 1px solid #e5e7eb;
        }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .brand-logo { color: #333; font-size: 24px; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title {
          font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;
          color: #F89423; padding: 0 20px; margin-bottom: 10px;
        }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link {
          display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none;
          transition: all 0.3s ease; font-weight: 500;
        }
        .nav-link:hover, .nav-link.active { 
          background-color: #F89423; 
          color: white; 
          border-left: 4px solid white; 
        }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; }
        
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { 
          background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100;
        }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .header-icon { 
          width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6;
          color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; 
        }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { 
          position: absolute; top: 5px; right: 5px; background: #F89423; color: white;
          font-size: 10px; padding: 2px 5px; border-radius: 10px; 
        }
        .user-avatar { 
          width: 40px; height: 40px; border-radius: 50%; 
          background: linear-gradient(135deg, #F89423 0%, #F89423 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 600; cursor: pointer; font-size: 14px;
        }
        
        .content-wrapper { padding: 30px; flex: 1; }
        .table th { background-color: #f9fafb; border-bottom: 1px solid #e5e7eb; font-weight: 600; font-size: 13px; color: #374151; }
        .table td { vertical-align: middle; border-bottom: 1px solid #f3f4f6; padding: 12px 16px; }
        .modal-overlay { animation: fadeIn 0.3s ease; }
        .modal-panel { animation: slideIn 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
          border-width: 0.15em;
          vertical-align: text-bottom;
        }
        
        @media (max-width: 992px) { 
          .sidebar { transform: translateX(-100%); } 
          .sidebar.sidebar-open { transform: translateX(0); } 
          .main-content { margin-left: 0; } 
        }
        @media (max-width: 768px) { 
          .content-wrapper { padding: 15px; } 
          .top-header { padding: 15px; } 
        }
      `}</style>
    </div>
  );
}

export default CompanyDocuments;